import EndpointUrl from "../core/apiEndpoints/endPoints";
import { clearToken, getToken } from "./token-store";

/**
 * IIDM-3064 T1 — the ONE logout path (profile menu, access-denied page, axios 401 handler).
 *
 * Before: each of the three sites navigated to `${API}/logout?token=<bearer>&…`, putting the
 * bearer token into a URL — browser history, the API's access log, any proxy log — and cleared a
 * different, incomplete set of storage keys. Now:
 *
 *   1. revoke the token at the SSO with `POST /api/token/revoke` + `Authorization: Bearer`
 *      (IIDM-2978; same contract as iidm_platform `revokeSsoToken`) — the token never enters a URL;
 *   2. clear every key the app writes;
 *   3. send the browser to the BE's PHASE-2 logout (`/logout?redirect&client_id&redirect_uri`),
 *      which forwards to the SSO's `dologout` so the SSO session cookie is cleared too. Phase 1
 *      (`?token=`) is skipped entirely, because step 1 already did its job.
 *
 * Never throws and never blocks on the SSO for more than REVOKE_TIMEOUT_MS: a slow or unreachable
 * SSO must not leave the user looking logged in.
 */
export const REVOKE_TIMEOUT_MS = 5000;
/** IIDM-3157 — short timeout for the cache-clear call; logout must not wait on it. */
export const FORGET_TIMEOUT_MS = 3000;

/** Every localStorage key the portal writes (grep of setLocalStorage/localStorage.setItem). */
export const APP_STORAGE_KEYS = [
  "token",
  "userPermission",
  "userType",
  "payload",
  "quoteData",
  "orderData",
  "requestInfo",
  "globalSearch",
  "optionID",
  "search",
  "filter",
  "filter_name",
  "pageNo",
  "gridResponse",
  "syncing",
  "leftkey",
];

/**
 * The SSO origin used for the revoke call. `REACT_APP_SSO_URL` (plan Q3 default) when set;
 * otherwise derived from `REACT_APP_SERVER_URL`, which is the SSO's `…/oauth` base in every
 * environment checked (verification-log 2026-09-28).
 *
 * @return {string} origin without a trailing slash, or "" when neither is configured
 */
export function getSsoOrigin(): string {
  const explicit = (process.env.REACT_APP_SSO_URL || "").trim();
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }
  const server = (process.env.REACT_APP_SERVER_URL || "").trim();
  if (!server) {
    return "";
  }

  return server.replace(/\/+$/, "").replace(/\/oauth$/, "");
}

/**
 * Revokes one access token at the SSO. 401 means already invalid/expired/revoked — nothing to do.
 *
 * @param {string} accessToken the bearer token to revoke
 * @return {Promise<boolean>} true only when the SSO confirmed the revocation
 */
export async function revokeSsoToken(accessToken: string): Promise<boolean> {
  const sso = getSsoOrigin();
  if (!accessToken || !sso || typeof fetch !== "function") {
    if (accessToken && !sso) {
      console.error("IIDM-3064: no SSO origin configured — cannot revoke the token.");
    }
    return false;
  }
  const controller = typeof AbortController === "function" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), REVOKE_TIMEOUT_MS) : undefined;
  try {
    const res = await fetch(`${sso}/api/token/revoke`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
      signal: controller ? controller.signal : undefined,
    });
    if (res.status === 401) {
      return false;
    }
    if (!res.ok) {
      console.error("IIDM-3064: SSO token revoke returned", res.status);
      return false;
    }

    return true;
  } catch (err) {
    console.error("IIDM-3064: SSO token revoke failed", err);
    return false;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/** Removes every app key from localStorage, and the token from sessionStorage. */
export function clearAppStorage(): void {
  clearToken();
  APP_STORAGE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  });
}

/**
 * The BE phase-2 logout URL. Carries NO token. `REACT_APP_API_URL` + `logoutApi` is the same
 * concatenation the three call sites used (see the IIDM-2883 note on `logoutApi`).
 *
 * @return {string} the URL the browser is sent to
 */
export function buildLogoutUrl(): string {
  const query = new URLSearchParams({
    redirect: "",
    client_id: `${process.env.REACT_APP_CLIENT_ID}`,
    redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}`,
  });

  return `${process.env.REACT_APP_API_URL}${EndpointUrl.logoutApi}?${query.toString()}`;
}

/**
 * IIDM-3157 — clears the BE's per-token SSO-answer cache so the token stops working immediately
 * rather than after the cache TTL (~60 s). Always fire-and-forget: a slow or unreachable BE must
 * not delay or break logout. Must run AFTER the SSO revoke (order matters — running before allows
 * a concurrent request to re-cache the still-valid token).
 *
 * @param {string} accessToken the token being retired
 */
export async function forgetToken(accessToken: string): Promise<void> {
  const apiUrl = (process.env.REACT_APP_API_URL || "").trim();
  if (!accessToken || !apiUrl || typeof fetch !== "function") return;
  const controller = typeof AbortController === "function" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), FORGET_TIMEOUT_MS) : undefined;
  try {
    await fetch(`${apiUrl}/v1/auth/forget`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: controller ? controller.signal : undefined,
    });
  } catch {
    // intentional — never throw, never block logout
  } finally {
    if (timer) clearTimeout(timer);
  }
}

let inFlight: Promise<void> | null = null;

/**
 * Revoke → clear → navigate. Concurrent callers (several requests 401 at once, or the menu and
 * the interceptor together) share one run.
 *
 * @return {Promise<void>} resolves after the navigation has been requested
 */
export function revokeAndLogout(): Promise<void> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = (async () => {
    try {
      const accessToken = getToken() || legacyToken();
      if (accessToken) {
        await revokeSsoToken(accessToken);
        await forgetToken(accessToken);
      }
    } finally {
      clearAppStorage();
      window.location.href = buildLogoutUrl();
    }
  })();
  // A navigation normally unloads the page; if it does not (tests, a blocked navigation), let a
  // later logout run again instead of returning a stale settled promise forever.
  inFlight.then(
    () => {
      inFlight = null;
    },
    () => {
      inFlight = null;
    }
  );

  return inFlight;
}

/**
 * A token left in localStorage by a pre-IIDM-3064 build, so a user upgraded mid-session still has
 * it revoked rather than merely deleted.
 *
 * @return {string} the legacy token or ""
 */
function legacyToken(): string {
  try {
    return window.localStorage.getItem("token") || "";
  } catch (e) {
    return "";
  }
}
